import { ArrowUp, Plus } from "@phosphor-icons/react";
import { forwardRef, type FormEvent, type ReactNode, type TextareaHTMLAttributes } from "react";
import styled from "@emotion/styled";
import { twRing } from "@/lib/emotionTw";
import { IconButton } from "../../actions/IconButton";

export type ChatComposerProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onSubmit"> & {
  value: string;
  onValueChange: (value: string) => void;
  onSubmit?: () => void;
  loading?: boolean;
  disabled?: boolean;
  /** `"sm"` is a compact composer for inline/sidebar placements; `"md"` is the default. */
  size?: "sm" | "md";
  attachmentSlot?: ReactNode;
  actionSlot?: ReactNode;
  autoHeight?: boolean;
  compactPadding?: string;
  compactActionButtonSize?: number | string;
  placeholderVariant?: "default" | "large";
  textareaClassName?: string;
  sendButtonClassName?: string;
};

/* Exact compiled output of the pre-migration Tailwind utilities. The compact
 * icon-button overrides use `&&` so they beat the Button's own size/disabled
 * rules — the same result tailwind-merge produced pre-migration. */

const ChatComposerRoot = styled("form", {
  label: "AgenticChatComposer-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { compact: boolean; autoHeight: boolean; compactPadding?: string } }>(({ ownerState }) => [
  {
    display: "flex",
    alignItems: "flex-end",
    borderRadius: "var(--bfrs-radius-lg)",
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    "--tw-shadow": "var(--bfrs-shadow-low)",
    "--tw-shadow-colored": "var(--bfrs-shadow-low)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
    "&:focus-within": {
      "--tw-border-opacity": "1",
      borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))",
      ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / .2)")
    }
  },
  // The compact variant's arbitrary 12px shadow class sat EARLIER in the
  // compiled stylesheet than shadow-low, so shadow-low always won the cascade
  // and the 12px shadow never took effect. Reproduced by not overriding the
  // base shadow here.
  ownerState.compact
    ? {
        height: ownerState.autoHeight ? "auto" : "42px",
        alignItems: ownerState.autoHeight ? "flex-end" : "center",
        gap: ".5rem",
        padding: ownerState.compactPadding ?? "6px"
      }
    : {
        minHeight: "3.5rem",
        gap: ".5rem",
        padding: ".5rem"
      }
]);

const ChatComposerTextarea = styled("textarea", {
  label: "AgenticChatComposer-textarea",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { compact: boolean; placeholderVariant: "default" | "large" } }>(({ ownerState }) => [
  {
    flex: "1 1 0%",
    resize: "none",
    backgroundColor: "transparent",
    paddingLeft: ".25rem",
    paddingRight: ".25rem",
    lineHeight: "1.25rem",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    "&::-moz-placeholder": {
      "--tw-text-opacity": "1",
      color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
    },
    "&::placeholder": {
      "--tw-text-opacity": "1",
      color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
    },
    "&:disabled": {
      cursor: "not-allowed"
    }
  },
  ownerState.compact
    ? {
        maxHeight: "7rem",
        minHeight: "1.5rem",
        paddingTop: "1px",
        paddingBottom: "1px",
        fontSize: "12px",
        lineHeight: "22.4px"
      }
    : {
        maxHeight: "9rem",
        minHeight: "2.25rem",
        paddingTop: ".5rem",
        paddingBottom: ".5rem",
        fontSize: "1rem",
        lineHeight: "1.5rem"
      },
  ownerState.placeholderVariant === "large"
    ? {
        ...(ownerState.compact ? { paddingLeft: "10px" } : {}),
        "&::-moz-placeholder": {
          fontSize: "14px",
          lineHeight: "20px",
          "--tw-text-opacity": "1",
          color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
          opacity: 1
        },
        "&::placeholder": {
          fontSize: "14px",
          lineHeight: "20px",
          "--tw-text-opacity": "1",
          color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
          opacity: 1
        }
      }
    : null
]);

const ChatComposerAttachButton = styled(IconButton, {
  label: "AgenticChatComposer-attachButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { compact: boolean } }>(({ ownerState }) => [
  ownerState.compact && {
    "&&": {
      width: "1.5rem",
      height: "1.5rem",
      borderRadius: "var(--bfrs-radius-lg)"
    }
  }
]);

const ChatComposerSendButton = styled(IconButton, {
  label: "AgenticChatComposer-sendButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { compact: boolean; compactActionButtonSize?: number | string } }>(({ ownerState }) => [
  ownerState.compact && {
    "&&": {
      width:
        ownerState.compactActionButtonSize === undefined
          ? "1.5rem"
          : typeof ownerState.compactActionButtonSize === "number"
            ? `${ownerState.compactActionButtonSize}px`
            : ownerState.compactActionButtonSize,
      height:
        ownerState.compactActionButtonSize === undefined
          ? "1.5rem"
          : typeof ownerState.compactActionButtonSize === "number"
            ? `${ownerState.compactActionButtonSize}px`
            : ownerState.compactActionButtonSize,
      borderRadius: "var(--bfrs-radius-lg)"
    },
    "&&:disabled": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
      opacity: 1
    }
  }
]);

export const ChatComposer = forwardRef<HTMLTextAreaElement, ChatComposerProps>(
  (
    {
      className,
      value,
      onValueChange,
      onSubmit,
      loading,
      disabled,
      size = "md",
      attachmentSlot,
      actionSlot,
      autoHeight = false,
      compactPadding,
      compactActionButtonSize,
      placeholderVariant = "default",
      textareaClassName,
      sendButtonClassName,
      placeholder = "Ask me anything",
      ...props
    },
    ref
  ) => {
    const compact = size === "sm";
    const iconSize = compact ? 14 : 18;
    const iconButtonSize = compact ? "xs" : "sm";

    const handleSubmit = (event: FormEvent) => {
      event.preventDefault();
      if (!disabled && !loading && value.trim()) onSubmit?.();
    };

    return (
      <ChatComposerRoot onSubmit={handleSubmit} ownerState={{ compact, autoHeight, compactPadding }} className={className}>
        {attachmentSlot ?? (
          <ChatComposerAttachButton
            aria-label="Attach file"
            type="button"
            icon={<Plus size={iconSize} />}
            variant={compact ? "secondary" : "ghost"}
            size={iconButtonSize}
            ownerState={{ compact }}
            disabled={disabled}
          />
        )}
        <ChatComposerTextarea
          ref={ref}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          rows={1}
          ownerState={{ compact, placeholderVariant }}
          className={textareaClassName}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              if (value.trim()) onSubmit?.();
            }
          }}
          {...props}
        />
        {actionSlot}
        <ChatComposerSendButton
          aria-label="Send message"
          type="submit"
          icon={<ArrowUp size={iconSize} weight="bold" />}
          variant="primary"
          size={iconButtonSize}
          ownerState={{ compact, compactActionButtonSize }}
          className={sendButtonClassName}
          loading={loading}
          disabled={disabled || !value.trim()}
        />
      </ChatComposerRoot>
    );
  }
);

ChatComposer.displayName = "ChatComposer";
